import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Modal, Text } from '@hopskipdrive/react-components';
import { zonedTimeToUtc } from 'date-fns-tz';
import { includes } from 'lodash';

import { Button } from '../../../shared/components/Button';
import { CopyToClipboard } from '../../../shared/components/CopyToClipboard';
import { SchoolBuildingIcon } from '../../../shared/components/Icons';
import { ButtonLink } from '../../../shared/components/Links';
import { colorPalette, colorTokens } from '../../../shared/components/variables';
import { useToast } from '../../../shared/contexts/ToastContext';
import { RideForm } from '../../components/RideForm';
import { RiderRemovalsModal } from '../../components/RiderRemovalsModal';
import { useRemoveRidersSubmit } from '../../components/RiderRemovalsModal/useRemoveRidersSubmit';
import { RIDE_STATES } from '../../constants/rideStates';
import { useEditRideSubmit } from '../../hooks/useEditRideSubmit';
import { EditRideDetails, EditRidePayload, RiderOnRide } from '../../types/rides';
import { OutletContextType } from '../PageHeading';

import { editRideValidationSchema } from './editRideValidationSchema';

function useEditRide() {
  return useOutletContext<OutletContextType>();
}

const rideFormDefaults = {
  anchorTime: new Date(),
  timeAnchor: 'drop_off',
  time: '',
  waypointsAttributes: [
    {
      anchor: false,
      id: 0,
      passengerIdsStrings: [],
      accountLocationAttributes: [
        {
          address: '',
          accountId: 0,
          placeId: '',
          streetAddressTwo: '',
        },
      ],
    },
    {
      anchor: true,
      id: 0,
      passengerIdsStrings: [],
      accountLocationAttributes: [
        {
          address: '',
          accountId: 0,
          placeId: '',
          streetAddressTwo: '',
        },
      ],
    },
  ],
};

export const EditRideFormModal = () => {
  const { rideSlug, ridersOnRide, editRideDetails, accountDetails, timeZone, cancelReasons, organizers, rideState } =
    useEditRide();
  const canEdit = !includes([...RIDE_STATES.completed, ...RIDE_STATES.inactive, ...RIDE_STATES.canceled], rideState);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<EditRidePayload>();
  const [ridersToRemove, setRidersToRemove] = useState<RiderOnRide[]>([]);
  const { mutate: editRideMutate, isPending } = useEditRideSubmit({
    rideSlug: rideSlug,
    successCallback: () => {
      addToast({
        severity: 'success',
        message: 'Ride Updated',
      });
      navigate('..');
    },
    errorCallback: (
      error: Error & {
        errors: string[];
      }
    ) => {
      error?.errors
        ? error?.errors?.forEach((errorMessage) => {
            addToast({
              severity: 'error',
              message: errorMessage,
            });
          })
        : addToast({
            severity: 'error',
            message: error.message,
          });
    },
  });
  const { mutate: removeRidersMutate, isPending: isPendingRemoveRiders } = useRemoveRidersSubmit({
    rideSlug: rideSlug,
    successCallback: () => {
      setRidersToRemove([]);
      formData && handleSubmit(formData);
    },
    errorCallback: (
      error: Error & {
        errors: string[];
      }
    ) => {
      error?.errors
        ? error?.errors?.forEach((errorMessage) => {
            addToast({
              severity: 'error',
              message: errorMessage,
            });
          })
        : addToast({
            severity: 'error',
            message: error.message,
          });
    },
  });
  const methods = useForm<EditRideDetails & { adjustmentReasonKey?: string }>({
    defaultValues: useMemo(() => {
      return { ...rideFormDefaults, ...editRideDetails };
    }, [editRideDetails]),
    resolver: yupResolver(editRideValidationSchema(timeZone, editRideDetails.time)),
  });

  const handleSubmit = async (data: EditRidePayload) => {
    let editRidePayload = { ...formData, ...data };

    // If there are riders on the ride, we need to remove them from the ride before updating the ride
    if (!formData && data) {
      const ridersOnRideIds = data.trip?.waypointsAttributes.map((waypoint) => waypoint.passengerIdsStrings).flat();
      const removedRiders = ridersOnRideIds?.length
        ? ridersOnRide.filter((rider) => !ridersOnRideIds?.includes(rider.id.toString()))
        : [];
      const removedWaypoints = data.trip?.waypointsAttributes?.length
        ? editRideDetails.waypointsAttributes
            .filter((waypoint) => !data.trip?.waypointsAttributes?.some((w) => w.id === waypoint.id))
            .map((waypoint) => ({
              id: waypoint.id,
              passengerIdsStrings: [],
              accountLocationAttributes: waypoint.accountLocationAttributes,
            }))
        : [];

      editRidePayload = {
        ...editRidePayload,
        trip: {
          ...data.trip,
          ...(data.trip?.anchorTime && {
            anchorTime: data.trip.anchorTime,
            originalAnchorTime: zonedTimeToUtc(editRideDetails.anchorTime, timeZone),
          }),
          waypointsAttributes: [
            ...removedWaypoints,
            ...(data.trip?.waypointsAttributes.map((waypoint) => {
              return {
                ...(waypoint.id && { id: waypoint.id }),
                anchor: waypoint.anchor,
                passengerIdsStrings: !waypoint.anchor ? waypoint.passengerIdsStrings : [],
                accountLocationAttributes: {
                  ...(waypoint.accountLocationAttributes?.accountId && {
                    accountId: waypoint.accountLocationAttributes?.accountId,
                  }),
                  address: waypoint.accountLocationAttributes?.address,
                  placeId: waypoint.accountLocationAttributes?.placeId,
                  streetAddressTwo: waypoint.accountLocationAttributes?.streetAddressTwo,
                },
              };
            }) || []),
          ],
        },
        ...(data.adjustmentReasonKey && { adjustmentReasonKey: data.adjustmentReasonKey }),
      };

      if (removedRiders.length > 0) {
        setRidersToRemove(removedRiders);
        setFormData(editRidePayload);

        return;
      }
    }

    editRideMutate(editRidePayload);
  };

  return (
    <>
      <Modal open={canEdit} maxWidth="lg" data-testid="edit-organizer-info">
        <Modal.Title>
          <Box display="flex" alignItems="center" gap="12px">
            <SchoolBuildingIcon style={{ display: 'block', height: '24px', width: '24px', alignSelf: 'center' }} />
            <Text variant="h6" textColor="primaryText">
              Account: {accountDetails.accountName}
            </Text>
            <CopyToClipboard
              toastMessage="Slug Copied"
              text={accountDetails.accountSlug}
              variant="h6"
              color={colorTokens.text.secondary}
            />
          </Box>
        </Modal.Title>
        <Modal.Content>
          <FormProvider {...methods}>
            <RideForm
              timeZone={timeZone}
              ridersOnRide={ridersOnRide}
              organizers={organizers}
              handleSubmit={({ adjustmentReasonKey, ...tripFormData }) =>
                handleSubmit({ trip: tripFormData, adjustmentReasonKey })
              }
              rideDetailsDefault={editRideDetails}
            />
          </FormProvider>
        </Modal.Content>
        <Modal.Actions style={{ borderTop: `1px solid ${colorPalette.neutralVariant90}` }}>
          <ButtonLink variant="text" to=".." disabled={isPending}>
            Never mind
          </ButtonLink>
          <Button type="submit" color="primary" form="ride-form" loading={isPending}>
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Actions>
      </Modal>
      {!!ridersToRemove.length && (
        <RiderRemovalsModal
          onClose={() => {
            setRidersToRemove([]);
            setFormData(undefined);
          }}
          reasons={cancelReasons}
          onRemove={removeRidersMutate}
          ridersToRemove={ridersToRemove}
          isPending={isPendingRemoveRiders}
          isEditRide={true}
        />
      )}
    </>
  );
};
